"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { PORTAL_MAX_WIDTH, portalFont } from "../partnerTheme";
import { dkStadiumConfig } from "../campaigns/draft-kings-stadium";

const cfg = dkStadiumConfig;
const ACCENT = cfg.accentColor;
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";

const NAV = [
  { id: "big-idea", label: "The concept" },
  { id: "format", label: "The format" },
  { id: "spaces", label: "The spaces" },
  { id: "tour", label: "The tour" },
  { id: "why-dk", label: "Why partner with us" },
  { id: "vision", label: "Vision" },
] as const;

/**
 * Stadium Tour walkthrough. Deck-sourced facts; proposed markets labeled clearly.
 */
export default function DraftKingsStadiumTourTab() {
  return (
    <Box>
      <Hero />
      <SubNav />
      <BigIdea />
      <Format />
      <Spaces />
      <Tour />
      <WhyDraftKings />
      <Vision />
    </Box>
  );
}

function Hero() {
  return (
    <Box sx={{ position: "relative", minHeight: { xs: "86vh", md: "90vh" }, display: "flex", alignItems: "stretch", bgcolor: "#000" }}>
      <MediaFill src={cfg.images.hero} alt="Stadium tour concept hero" objectPosition="center 40%" />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: {
            xs: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.94) 100%)",
            md: "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.65) 42%, rgba(0,0,0,0.25) 72%, rgba(0,0,0,0.55) 100%), linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.88) 100%)",
          },
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: PORTAL_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          py: { xs: 8, md: 12 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: "86vh", md: "90vh" },
        }}
      >
        <FadeUp>
          <Eyebrow accent>{cfg.timingLabel} · Proposed concept</Eyebrow>
          <Typography
            component="h1"
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: "clamp(2.3rem, 5.5vw + 0.4rem, 4.4rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.035em",
              maxWidth: 720,
              mb: 2,
            }}
          >
            ComedyUO{" "}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              Stadium Tour.
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.82)", fontSize: { xs: "1.05rem", md: "1.2rem" }, lineHeight: 1.45, maxWidth: 520, mb: 3 }}>
            {cfg.positioning}
          </Typography>
          <Button href="#big-idea" sx={primaryBtnSx}>
            Enter the walkthrough
          </Button>
        </FadeUp>
      </Box>
    </Box>
  );
}

function SubNav() {
  return (
    <Box sx={{ position: "sticky", top: { xs: 64, md: 72 }, zIndex: 30, bgcolor: "rgba(0,0,0,0.82)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${LINE}` }}>
      <Box sx={{ maxWidth: PORTAL_MAX_WIDTH, mx: "auto", px: { xs: 2.5, md: 4 }, py: 1.25, display: "flex", gap: 2, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        {NAV.map((item) => (
          <Box
            key={item.id}
            component="a"
            href={`#${item.id}`}
            sx={{
              fontFamily: portalFont,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: MUTED,
              whiteSpace: "nowrap",
              textDecoration: "none",
              "&:hover": { color: "#fff" },
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function BigIdea() {
  return (
    <Section id="big-idea">
      <Eyebrow accent>The concept</Eyebrow>
      <Title>
        A big-stage setting.{" "}
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          An intimate-show feeling.
        </Box>
      </Title>
      <Body sx={{ maxWidth: 680, mb: 3 }}>
        Stadiums stay culturally powerful even when no game is being played. ComedyUO unlocks the rooms fans rarely experience and turns them into close-up comedy nights with premium hospitality around them.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        <Photo src={cfg.images.locker} alt="Stadium interior concept" sx={{ minHeight: { xs: 240, md: 380 } }} />
        <Photo src={cfg.images.field} alt="Stadium field concept" sx={{ minHeight: { xs: 240, md: 380 } }} />
      </Box>
    </Section>
  );
}

function Format() {
  return (
    <Section id="format">
      <Eyebrow accent>The format</Eyebrow>
      <Title>Arrive. Connect. Laugh. Stay.</Title>
      <Body sx={{ maxWidth: 720, mb: 3 }}>
        Each city follows a similar arc. ComedyUO activates one or multiple spaces in a stadium besides the field producing simultaneous comedy shows for guests to enjoy. Meanwhile the talent rotates room to room to hit their spots as the night progresses. After the shows, guests get a full tour and can mingle afterwards.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.25 }}>
        {cfg.formatSteps.map((step, idx) => (
          <Box key={step.id} sx={{ p: 2.25, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG, minHeight: 180 }}>
            <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
              0{idx + 1}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, mb: 1 }}>{step.title}</Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.9rem", lineHeight: 1.45 }}>{step.body}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function Spaces() {
  return (
    <Section id="spaces">
      <Eyebrow accent>The spaces</Eyebrow>
      <Title>Venues within the stadium.</Title>
      <Body sx={{ maxWidth: 640, mb: 3 }}>
        Proposed room types and planning capacities from the Stadium Tour concept. Final rooms and ticket counts depend on each team and venue.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(5, 1fr)" }, gap: 1.25 }}>
        {cfg.spaces.map((space) => (
          <Box key={space.id} sx={{ p: 2.25, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG, minHeight: 170 }}>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, mb: 0.75 }}>{space.name}</Typography>
            <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontSize: "0.85rem", mb: 1 }}>{space.capacityLabel}</Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.88rem", lineHeight: 1.45 }}>{space.detail}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function Tour() {
  return (
    <Section id="tour">
      <Eyebrow accent>The tour</Eyebrow>
      <Title>Six stadiums and counting.</Title>
      <Body sx={{ maxWidth: 680, mb: 3 }}>
        Proposed footprint for April to June 2027. ComedyUO has met with each team below to discuss financing, dates and logistics. They are qualified to host and waiting on sponsorship details.
      </Body>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 1.5 }}>
        {cfg.markets.map((market) => (
          <Box key={market.id} sx={{ borderRadius: RADIUS, overflow: "hidden", border: `1px solid ${LINE}`, bgcolor: CARD_BG, display: "flex", flexDirection: "column" }}>
            <Box sx={{ position: "relative", height: 150, bgcolor: "#151515" }}>
              <MediaFill src={market.imageUrl} alt={`${market.stadium}`} />
              <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.75) 100%)" }} />
            </Box>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {market.city}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, fontSize: "1.15rem" }}>
                {market.team}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.9rem" }}>{market.stadium}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function WhyDraftKings() {
  const points = [
    {
      title: "Brand fit",
      body: "DraftKings turns sports fandom into participation. ComedyUO brings that energy inside major-league stadiums.",
    },
    {
      title: "Tour connection",
      body: "DraftKings can connect the full tour while creating market-specific moments around each city, stadium and fan base.",
    },
    {
      title: "Audience to reach",
      body: "Sports fans, comedy fans and young professionals who spend on live entertainment and premium experiences.",
    },
    {
      title: "VIP offerings",
      body: "Exclusive access for DraftKings VIPs to meet the talent, go out to dinner with them, collect signed memorabilia from each team, or get box seats to games that upcoming season.",
    },
  ];
  return (
    <Section id="why-dk">
      <Eyebrow accent>Why partner with us</Eyebrow>
      <Title>
        DraftKings lives at the intersection of sports, entertainment and fandom. This tour gives the brand a new way to bring fans inside iconic stadiums.
      </Title>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.25, mt: 1 }}>
        {points.map((point, idx) => (
          <Box key={point.title} sx={{ p: 2.5, borderRadius: RADIUS, border: `1px solid ${LINE}`, bgcolor: CARD_BG }}>
            <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
              0{idx + 1}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, mb: 1 }}>{point.title}</Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem", lineHeight: 1.5 }}>{point.body}</Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function Vision() {
  return (
    <Section id="vision">
      <Box
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: RADIUS,
          border: `1px solid ${LINE}`,
          background: `linear-gradient(135deg, color-mix(in srgb, ${ACCENT} 16%, transparent), rgba(255,255,255,0.03))`,
          textAlign: "center",
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: "clamp(1.7rem, 2.2vw + 1rem, 2.6rem)",
            lineHeight: 1.12,
            letterSpacing: "-0.03em",
          }}
        >
          Let's work together.
        </Typography>
      </Box>
    </Section>
  );
}

function Section({ id, children }: { id: string; children: ReactNode }) {
  return (
    <FadeUp>
      <Box id={id} component="section" sx={{ maxWidth: PORTAL_MAX_WIDTH, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 6, md: 9 }, scrollMarginTop: 140 }}>
        {children}
      </Box>
    </FadeUp>
  );
}

function Eyebrow({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return (
    <Typography sx={{ fontFamily: portalFont, fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: accent ? ACCENT : MUTED, mb: 1.25 }}>
      {children}
    </Typography>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <Typography component="h2" sx={{ fontFamily: portalFont, fontWeight: 700, color: "#fff", fontSize: "clamp(1.7rem, 2.2vw + 1rem, 2.6rem)", lineHeight: 1.12, letterSpacing: "-0.03em", mb: 1.5, maxWidth: 820 }}>
      {children}
    </Typography>
  );
}

function Body({ children, sx }: { children: ReactNode; sx?: object }) {
  return <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: { xs: "0.98rem", md: "1.05rem" }, lineHeight: 1.55, ...sx }}>{children}</Typography>;
}

function Photo({ src, alt, sx }: { src: string; alt: string; sx?: object }) {
  return (
    <Box sx={{ position: "relative", borderRadius: RADIUS, overflow: "hidden", border: `1px solid ${LINE}`, bgcolor: "#151515", ...sx }}>
      <MediaFill src={src} alt={alt} />
    </Box>
  );
}

function MediaFill({ src, alt, objectPosition = "center" }: { src: string; alt: string; objectPosition?: string }) {
  if (!src) {
    return <Box sx={{ position: "absolute", inset: 0, bgcolor: "#171717", backgroundImage: `radial-gradient(circle at 30% 20%, ${ACCENT}28, transparent 55%)` }} aria-label={alt} />;
  }
  return <Box component="img" src={src} alt={alt} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition }} />;
}

function FadeUp({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <Box ref={ref} sx={{ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(16px)", transition: "opacity 650ms ease, transform 650ms ease" }}>{children}</Box>;
}

const primaryBtnSx = {
  fontFamily: portalFont,
  fontWeight: 600,
  textTransform: "none" as const,
  color: "#0a0a0a",
  bgcolor: "#fff",
  borderRadius: "999px",
  px: 2.5,
  py: 1.1,
  alignSelf: "flex-start",
  "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
};
